import { useMemo } from 'react';
import { useQueryParamHelper } from '@shared/core/queryString';
import { UtlQueryParams } from '@shared/core/router.helper/RoutesContext';
import type { PartialUtmParameters } from './utmParameters.utils';
import { mergeUtmParameters } from './utmParameters.utils';
import { isUtmMedium } from './utmParameters.constants';

/**
 * Hook to extract UTM parameters from the current URL, optionally merging with internal UTM parameters.
 * Returns partial UTM parameters (allows partial params, unlike RoutesProvider which requires all 3).
 *
 * When `internalUtm` is provided, merges current URL UTM parameters with internal UTM parameters.
 * Current URL UTM parameters take precedence over internal UTM parameters to preserve campaign/ads tracking.
 *
 * @param internalUtm - Optional internal UTM parameters to merge with current URL UTM parameters
 * @returns Merged UTM parameters from current URL and internal UTM (if provided), or undefined if none exist
 */
export const useUtmParameters = (internalUtm?: PartialUtmParameters): PartialUtmParameters | undefined => {
  const queryParamHelper = useQueryParamHelper();

  return useMemo(() => {
    const utm_medium = queryParamHelper.getValueString(UtlQueryParams.utm_medium);
    const utm_source = queryParamHelper.getValueString(UtlQueryParams.utm_source);
    const utm_campaign = queryParamHelper.getValueString(UtlQueryParams.utm_campaign);
    const utm_source_secondary = queryParamHelper.getValueString('utm_source_secondary' as const);

    // Work around readonly properties by creating a new object via spreading
    const currentUrlUtm: PartialUtmParameters = {
      ...(isUtmMedium(utm_medium) ? { utm_medium } : {}),
      ...(utm_source && { utm_source }),
      ...(utm_campaign && { utm_campaign }),
      ...(utm_source_secondary && { utm_source_secondary }),
    };

    // If no current URL UTM params and no internal UTM provided, return undefined
    if (!currentUrlUtm.utm_medium && !currentUrlUtm.utm_source && !currentUrlUtm.utm_campaign && !currentUrlUtm.utm_source_secondary && !internalUtm) {
      return undefined;
    }

    // If internalUtm is provided, merge (current URL takes precedence)
    if (internalUtm) {
      const merged = mergeUtmParameters(currentUrlUtm, internalUtm);
      // Return undefined only if merged result is completely empty
      if (!merged.utm_medium && !merged.utm_source && !merged.utm_campaign && !merged.utm_source_secondary) {
        return undefined;
      }
      return merged;
    }

    // No internal UTM provided, return current URL UTM only (backward compatible)
    return Object.keys(currentUrlUtm).length > 0 ? currentUrlUtm : undefined;
  }, [queryParamHelper, internalUtm]);
};
